import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { CommandAlkonConnection } from '~hooks/useCommandAlkon/models';
import useCommandAlkon from '~hooks/useCommandAlkon/useCommandAlkon';
import { readFirstError } from '~queries/utils';
import useSingleFlight from '~utils/hooks/useSingleFlight';

const required = (labelKey: string) =>
  t('form_validation_errors.required', { field: t(labelKey) });

const connectSchema = yup.object().shape({
  entityRef: yup
    .string()
    .trim()
    .required(required('administration.integration.command_alkon.form.entity_ref_label')),
  clientId: yup
    .string()
    .trim()
    .required(required('administration.integration.command_alkon.form.client_id_label')),
  clientSecret: yup
    .string()
    .trim()
    .required(
      required('administration.integration.command_alkon.form.client_secret_label'),
    ),
  apiScopeRef: yup
    .string()
    .trim()
    .required(
      required('administration.integration.command_alkon.form.api_scope_ref_label'),
    ),
  apiKey: yup
    .string()
    .trim()
    .required(required('administration.integration.command_alkon.form.api_key_label')),
});

const replaceSchema = connectSchema.shape({
  clientSecret: yup.string().trim().default(''),
  apiKey: yup.string().trim().default(''),
});

type FormDto = yup.InferType<typeof connectSchema>;

// Command Alkon's login rejects the credential set as a whole, so the API flags every field.
const credentialFields = new Set([
  'api_key',
  'api_scope_ref',
  'client_id',
  'client_secret',
  'entity_ref',
]);

interface CommandAlkonConnectFormProps {
  companyId: string;
  connection?: CommandAlkonConnection;
  mode: 'connect' | 'replace';
  onCancel?: () => void;
  onSaved: (connection: CommandAlkonConnection) => Promise<void>;
}

export default function CommandAlkonConnectForm({
  companyId,
  connection,
  mode,
  onCancel,
  onSaved,
}: CommandAlkonConnectFormProps) {
  const { createConnection, updateConnection } = useCommandAlkon();
  const { isInFlight: isSaving, runSingleFlight } = useSingleFlight();
  const isReplace = mode === 'replace';

  const [rejection, setRejection] = useState<string | null>(null);

  const { control, handleSubmit } = useForm<FormDto>({
    resolver: yupResolver(isReplace ? replaceSchema : connectSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      apiKey: '',
      apiScopeRef: connection?.apiScopeRef ?? '',
      clientId: connection?.clientId ?? '',
      clientSecret: '',
      entityRef: connection?.entityRef ?? '',
    },
  });

  const handleSave = handleSubmit((formData) =>
    runSingleFlight(async () => {
      setRejection(null);
      try {
        const saved = isReplace
          ? await updateConnection(companyId, {
              apiScopeRef: formData.apiScopeRef,
              clientId: formData.clientId,
              entityRef: formData.entityRef,
              ...(formData.apiKey && { apiKey: formData.apiKey }),
              ...(formData.clientSecret && { clientSecret: formData.clientSecret }),
            })
          : await createConnection(companyId, formData);
        if (saved) await onSaved(saved);
      } catch (error) {
        const rejected = readFirstError(error);
        const serverMessage = credentialFields.has(rejected?.field ?? '')
          ? undefined
          : rejected?.message;
        setRejection(
          serverMessage ?? t('error_messages.command_alkon.connection_rejected'),
        );
      }
    }),
  );

  return (
    <Box
      component="form"
      autoComplete="off"
      noValidate
      onSubmit={handleSave}
      sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '560px' }}
    >
      <TextInput
        control={control}
        dataUi="command-alkon-entity-ref"
        isDisabled={isSaving}
        isRequired
        label={t('administration.integration.command_alkon.form.entity_ref_label')}
        name="entityRef"
      />

      <TextInput
        control={control}
        dataUi="command-alkon-client-id"
        isDisabled={isSaving}
        isRequired
        label={t('administration.integration.command_alkon.form.client_id_label')}
        name="clientId"
      />

      <TextInput
        control={control}
        dataUi="command-alkon-client-secret"
        isDisabled={isSaving}
        isRequired={!isReplace}
        label={t('administration.integration.command_alkon.form.client_secret_label')}
        name="clientSecret"
        slotProps={{ htmlInput: { type: 'password' } }}
      />

      <TextInput
        control={control}
        dataUi="command-alkon-api-scope-ref"
        isDisabled={isSaving}
        isRequired
        label={t('administration.integration.command_alkon.form.api_scope_ref_label')}
        name="apiScopeRef"
      />

      <TextInput
        control={control}
        dataUi="command-alkon-api-key"
        isDisabled={isSaving}
        isRequired={!isReplace}
        label={t('administration.integration.command_alkon.form.api_key_label')}
        name="apiKey"
        slotProps={{ htmlInput: { type: 'password' } }}
      />

      {isReplace ? (
        <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: -2 }}>
          {t('administration.integration.command_alkon.form.replace_helper')}
        </Typography>
      ) : null}

      {rejection ? <Alert severity="error">{rejection}</Alert> : null}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <SmallLoadingButton color="brandV2Yellow" loading={isSaving} type="submit">
          {t('administration.integration.command_alkon.cta.connect')}
        </SmallLoadingButton>
        {onCancel ? (
          <SmallButton
            color="brandV2Black"
            disabled={isSaving}
            onClick={onCancel}
            sx={{ borderColor: 'divider', color: 'text.primary' }}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>
        ) : null}
      </Box>
    </Box>
  );
}
